import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isDefined, isEmpty, isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks/context';
import { sharedABQueryKeys } from 'pages/audience/shared/utilities';
import { audienceService } from 'services';
import { selectUserId } from 'store/selectors';
import { AudienceStatus, IAudienceShop, MultiRfmSearchApi } from 'types/api/audience';
import { IResult } from 'types/api/common';
import { SaveForm } from 'types/pages/audience-builder/shared/save';

export const useUpdatePreBuilt = () => {
  const userId = useAppSelector(selectUserId);

  const queryClient = useQueryClient();
  const data = useSaveStoreSelector(({ data }) => data);

  return useMutation({
    mutationFn: async (formData: SaveForm) => {
      const { id, type } = formData;

      if (isNotDefined(id)) {
        return Promise.reject();
      }

      if (isNotDefined(userId)) {
        return Promise.reject();
      }

      if (type !== 'pre-built' || !isOfType<IAudienceShop>(data, 'categoryId')) {
        return Promise.reject();
      }

      const { shopId } = formData;

      if (isNotDefined(shopId)) {
        return;
      }

      const { client, description, giftDateEnd, giftDateStart, ...rest } = formData;
      const shop: IAudienceShop = {
        ...data,
        ...rest,
        clientId: client?.id ?? null,
        clientName: client?.name ?? null,
        description: isEmpty(description) ? null : description,
        giftDateEnd: giftDateEnd?.toISOString(),
        giftDateStart: giftDateStart?.toISOString(),
        id,
        quantity: 0,
        shopId,
        statusId: AudienceStatus.Active,
        userId,
      };

      const { parameters } = shop;

      if (isOfType<MultiRfmSearchApi>(parameters, 'rfmSets')) {
        return await audienceService.updatePreBuiltMultiRfm(id, shop);
      }

      return await audienceService.updateMasterFile(id, shop);
    },
    onError: (_error, formData: SaveForm, context) => {
      const { type } = formData;

      if (type === 'pre-built') {
        const { shopId } = formData;

        if (isDefined(shopId)) {
          queryClient.setQueriesData({ queryKey: sharedABQueryKeys.shop.default(shopId) }, context?.snapshot);
        }
      }
    },
    onMutate: async (formData: SaveForm) => {
      const { type } = formData;

      if (type !== 'pre-built') {
        return;
      }

      const { shopId } = formData;

      if (isNotDefined(shopId)) {
        return;
      }

      const shopQueryKey = sharedABQueryKeys.shop.default(shopId);

      await queryClient.cancelQueries({ queryKey: shopQueryKey });

      const snapshot = queryClient.getQueriesData<IResult<IAudienceShop>>({ queryKey: shopQueryKey });

      queryClient.setQueriesData<IResult<IAudienceShop>>({ queryKey: shopQueryKey }, (oldData) => {
        if (isDefined(oldData)) {
          const { dataContainer } = oldData;

          if (isNotDefined(dataContainer)) {
            return oldData;
          }

          const {
            client: { id: clientId, name: clientName },
            giftDateEnd,
            giftDateStart,
            ...shop
          } = formData;

          oldData = {
            ...oldData,
            dataContainer: {
              ...dataContainer,
              ...shop,
              clientId,
              clientName,
              giftDateEnd: giftDateEnd?.toISOString(),
              giftDateStart: giftDateStart?.toISOString(),
            },
          };

          return oldData;
        }

        return oldData;
      });

      return {
        snapshot,
      };
    },
    onSuccess: (result, formData) => {
      if (result?.success) {
        if (isOfType<IAudienceShop>(formData, 'categoryId') && isDefined(formData.shopId)) {
          queryClient.invalidateQueries({ queryKey: sharedABQueryKeys.shop.default(formData.shopId) });
        }
      }
    },
  });
};
